import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import { captureTypes } from '../../utils/constants';
import { getFormikError } from '../../utils/misc';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { TextField, InputAdornment, Switch, Checkbox, DialogContentText, Grid, MenuItem } from '@mui/material';
import { useTheme } from '@emotion/react';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useFormik } from 'formik';
import * as Yup from 'yup';

function UpdateDeviceDialog(props) {
    const { open, captureDevice, onClose, handleSubmit } = props;
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            dailyUploadLimit: _.get(captureDevice, 'configuration.dailyUploadLimit', '') / 1000000,
            captureType: _.get(captureDevice, 'configuration.captureType', ''),
            motionConfig: {
                width: _.get(captureDevice, 'configuration.motionConfig.width', ''),
                height: _.get(captureDevice, 'configuration.motionConfig.height', ''),
                framerate: _.get(captureDevice, 'configuration.motionConfig.framerate', ''),
                threshold: _.get(captureDevice, 'configuration.motionConfig.threshold', '')
            }
        },
        validationSchema: Yup.object({
            dailyUploadLimit: Yup.number().required('You must enter a value'),
            captureType: Yup.string().required('You must enter a value'),
            motionConfig: Yup.object({
                width: Yup.number().integer('This must be a whole number').required('You must enter a value'),
                height: Yup.number().integer('This must be a whole number').required('You must enter a value'),
                framerate: Yup.number()
                    .integer('This must be a whole number')
                    .min(2, 'This cannot be lower than 2')
                    .required('You must enter a value'),
                threshold: Yup.number().required('You must enter a value')
            })
        }),
        onSubmit: (values) => {
            const updateObj = {};

            updateObj['configuration.dailyUploadLimit'] = values.dailyUploadLimit * 1000000;
            updateObj['configuration.motionConfig.width'] = values.motionConfig.width;
            updateObj['configuration.motionConfig.height'] = values.motionConfig.height;
            updateObj['configuration.motionConfig.framerate'] = values.motionConfig.framerate;
            updateObj['configuration.motionConfig.threshold'] = values.motionConfig.threshold;
            updateObj['configuration.captureType'] = values.captureType;
            if (values.captureType === 'Image') {
                updateObj['configuration.motionConfig.picture_output'] = 'on';
                updateObj['configuration.motionConfig.movie_output'] = 'off';
            } else {
                updateObj['configuration.motionConfig.picture_output'] = 'off';
                updateObj['configuration.motionConfig.movie_output'] = 'on';
            }

            handleSubmit(updateObj);
        }
    });

    useEffect(() => {
        if (open) {
            formik.handleReset();
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
            <DialogTitle>Configure Device</DialogTitle>
            <DialogContent style={{ paddingTop: theme.spacing(2) }}>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            {...formik.getFieldProps('dailyUploadLimit')}
                            fullWidth
                            type="number"
                            label="Daily Upload Limit (MB)"
                            margin="normal"
                            error={getFormikError(formik, 'dailyUploadLimit')}
                            helperText={getFormikError(formik, 'dailyUploadLimit')}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            {...formik.getFieldProps('captureType')}
                            label="Capture Type"
                            margin="normal"
                            error={getFormikError(formik, 'captureType')}
                            helperText={getFormikError(formik, 'captureType')}
                            fullWidth
                            select
                        >
                            {captureTypes.map((captureType) => (
                                <MenuItem key={captureType} value={captureType}>
                                    {captureType}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            {...formik.getFieldProps('motionConfig.threshold')}
                            fullWidth
                            type="number"
                            label="Threshold (pixels)"
                            margin="normal"
                            error={getFormikError(formik, 'motionConfig.threshold')}
                            helperText={getFormikError(formik, 'motionConfig.threshold')}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            {...formik.getFieldProps('motionConfig.framerate')}
                            fullWidth
                            type="number"
                            label="Frame Rate"
                            margin="normal"
                            error={getFormikError(formik, 'motionConfig.framerate')}
                            helperText={getFormikError(formik, 'motionConfig.framerate')}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            {...formik.getFieldProps('motionConfig.width')}
                            fullWidth
                            type="number"
                            label="Image Width (pixels)"
                            margin="normal"
                            error={getFormikError(formik, 'motionConfig.width')}
                            helperText={
                                getFormikError(formik, 'motionConfig.width') ||
                                'Valid range is camera dependent. Must be multiple of 8.'
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            {...formik.getFieldProps('motionConfig.height')}
                            fullWidth
                            type="number"
                            label="Image Height (pixels)"
                            margin="normal"
                            error={getFormikError(formik, 'motionConfig.height')}
                            helperText={
                                getFormikError(formik, 'motionConfig.height') ||
                                'Valid range is camera dependent. Must be multiple of 8.'
                            }
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button color="primary" onClick={formik.handleSubmit}>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default UpdateDeviceDialog;
